import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'

import type { IssueSeverity } from '@/shared/constants/issue-status'

import type { IssueGroupBy } from '../lib/issue-groups'
import type { IssueStatusView } from '../lib/issues'

/**
 * Shared UI state for the GIS Issues board: the merged status view (All / Open / Closed) plus the
 * faceted working set (severity / category / entity / department / HOD-department), the free-text
 * search query, the list grouping + List/Gallery view (V5 toolbar layout), and the row-hovered site
 * id. The map overlay AND the panel board both read this, so they cross-filter in lockstep.
 * Multi-select per facet: each facet is an array, `[]` = "all". Values within a facet union (OR);
 * different facets AND. Toggling a value adds it if absent, removes it if present. Ephemeral —
 * intentionally NOT persisted.
 *
 * `hod` holds DEPARTMENT names (the HOD picker's values — HOD ↔ department is 1:1, and the issue
 * feed only carries `department`), so filtering works even before the HOD-directory endpoint ships.
 *
 * `hoveredSiteId` / `focusedSiteId` are the map's shared hover/fly-to signals — the Coverage board
 * writes them too (its own facet lives in `gisCoverageViewStore`).
 */
interface GisIssueViewState {
  statusView: IssueStatusView
  severity: IssueSeverity[]
  category: string[]
  /** Selected HEADINGS from the curated "Issue type" dropdown (`issue-type-catalog.ts`) — e.g.
   *  "Staff", "Infra". Deliberately separate from `category` — this dropdown is the ONLY surface
   *  using the curated catalog; `category` (section_title) still drives Group by / the Dashboard
   *  donut / chips as before. Selecting a heading (`toggleIssueType`) also defaults `groupBy` to
   *  'question', so the list's Group-by rail immediately shows that heading's specific questions. */
  issueType: string[]
  entity: string[]
  department: string[]
  /** HOD facet — stores the selected HODs' department names (see doc-block above). */
  hod: string[]
  /** Free-text search over title / ref / site / entity / department / issue type. */
  query: string
  /** Pending-age band keys (`w1`…`m1`) — the age filter carried in from the dashboard's age chart. */
  ageBands: string[]
  /** Site names — the site filter carried in from the dashboard's Site breakdown. */
  siteNames: string[]
  groupBy: IssueGroupBy
  listView: 'list' | 'gallery'
  hoveredSiteId: string | null
  /**
   * The site the user is examining via an open issue in the board — the map flies to it and highlights
   * its pin. Distinct from `hoveredSiteId` (transient mouse hover) and from the drill selection
   * (`useGisSelection`), so clicking an issue row focuses the map WITHOUT opening the drill overlay.
   */
  focusedSiteId: string | null
}

interface GisIssueViewStore extends GisIssueViewState {
  setStatusView: (view: IssueStatusView) => void
  /** Toggle a value in/out of its facet (add if absent, remove if present). */
  toggleSeverity: (severity: IssueSeverity) => void
  toggleCategory: (category: string) => void
  /** Toggle a heading in/out of the curated "Issue type" dropdown's selection. Selecting one
   *  (unselected → selected) also defaults `groupBy` to 'question'. */
  toggleIssueType: (heading: string) => void
  toggleEntity: (entity: string) => void
  toggleDepartment: (department: string) => void
  toggleHod: (department: string) => void
  setQuery: (query: string) => void
  /** Toggle one pending-age band in/out of the age filter. */
  toggleAgeBand: (key: string) => void
  /** Replace the age filter wholesale (the dashboard "Show filters" hand-off). */
  setAgeBands: (keys: string[]) => void
  /** Toggle one site name in/out of the site filter. */
  toggleSiteName: (name: string) => void
  /** Apply the dashboard's whole staged filter at once (the "Show issues" hand-off — replaces each
   *  facet, so an unselected dimension clears on the board). */
  applyFilters: (f: {
    severity: IssueSeverity[]
    category: string[]
    department: string[]
    hod: string[]
    site: string[]
    age: string[]
  }) => void
  setGroupBy: (groupBy: IssueGroupBy) => void
  setListView: (view: 'list' | 'gallery') => void
  setHoveredSite: (id: string | null) => void
  setFocusedSite: (id: string | null) => void
  /** Clear every facet (severity + category + entity + department + hod) AND the search query;
   *  keeps the status view (the chip bar resets that separately). */
  clearFacets: () => void
  reset: () => void
}

const initialState: GisIssueViewState = {
  statusView: 'open', // the merged "Open" = pending + in-review (every non-closed status)
  severity: [],
  category: [],
  issueType: [],
  entity: [],
  department: [],
  hod: [],
  query: '',
  ageBands: [],
  siteNames: [],
  groupBy: 'site', // issues are grouped by their site by default (the "Group" control changes it)
  listView: 'gallery', // Gallery is the default presentation; List is the secondary view
  hoveredSiteId: null,
  focusedSiteId: null,
}

/** Add `value` to `arr` if missing, else drop it — the toggle spine every facet setter shares. */
function toggleValue<T>(arr: T[], value: T): T[] {
  return arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value]
}

export const useGisIssueView = create<GisIssueViewStore>()(
  devtools(
    immer((set) => ({
      ...initialState,

      setStatusView: (view) =>
        set((s) => {
          s.statusView = view
        }),

      toggleSeverity: (severity) =>
        set((s) => {
          s.severity = toggleValue(s.severity, severity)
        }),

      toggleCategory: (category) =>
        set((s) => {
          s.category = toggleValue(s.category, category)
        }),

      toggleIssueType: (heading) =>
        set((s) => {
          const wasSelected = s.issueType.includes(heading)
          s.issueType = toggleValue(s.issueType, heading)
          if (!wasSelected) {
            // Just selected a heading — default the list to Group by Question so its rail
            // immediately shows that heading's specific curated questions.
            s.groupBy = 'question'
          }
        }),

      toggleEntity: (entity) =>
        set((s) => {
          s.entity = toggleValue(s.entity, entity)
        }),

      toggleDepartment: (department) =>
        set((s) => {
          s.department = toggleValue(s.department, department)
        }),

      toggleHod: (department) =>
        set((s) => {
          s.hod = toggleValue(s.hod, department)
        }),

      setQuery: (query) =>
        set((s) => {
          s.query = query
        }),

      toggleAgeBand: (key) =>
        set((s) => {
          s.ageBands = toggleValue(s.ageBands, key)
        }),

      setAgeBands: (keys) =>
        set((s) => {
          s.ageBands = keys
        }),

      toggleSiteName: (name) =>
        set((s) => {
          s.siteNames = toggleValue(s.siteNames, name)
        }),

      applyFilters: (f) =>
        set((s) => {
          s.severity = f.severity
          s.category = f.category
          s.department = f.department
          s.hod = f.hod
          s.siteNames = f.site
          s.ageBands = f.age
        }),

      setGroupBy: (groupBy) =>
        set((s) => {
          s.groupBy = groupBy
        }),

      setListView: (view) =>
        set((s) => {
          s.listView = view
        }),

      setHoveredSite: (id) =>
        set((s) => {
          s.hoveredSiteId = id
        }),

      setFocusedSite: (id) =>
        set((s) => {
          s.focusedSiteId = id
        }),

      clearFacets: () =>
        set((s) => {
          s.severity = []
          s.category = []
          s.issueType = []
          s.entity = []
          s.department = []
          s.hod = []
          s.query = ''
          s.ageBands = []
          s.siteNames = []
        }),

      reset: () => set(() => ({ ...initialState })),
    })),
    { name: 'useGisIssueView' },
  ),
)
